import { useMutation } from '@apollo/client';
import { FlagTransition } from '@generated/graphql';
import type { Tokens } from '@ovComponents/0-tokens/useTokens';
import { useGlobalToast } from '@providers/globalToastProvider';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TRANSITION_FLAG, UPDATE_FLAG_NOTES } from './flags.queries';
import {
  areFlagNotesAtLeastAsFresh,
  hasUnsavedFlagNoteDraft,
} from './reconFlagNotes';
import type { ReconFlagRow } from './reconFlags.types';

type UseReconFlagsDrawerInput = {
  sys: Tokens['sys'];
  t: TFunction;
  refetchAfterFlagMutation: () => Promise<unknown>;
  refetchGrid: () => Promise<unknown>;
};

type DrawerTransitionAction = 'resolve' | 'dismiss';

export const useReconFlagsDrawer = ({
  sys,
  t,
  refetchAfterFlagMutation,
  refetchGrid,
}: UseReconFlagsDrawerInput) => {
  const { showToast } = useGlobalToast();
  const [activeDrawerFlag, setActiveDrawerFlag] = useState<ReconFlagRow | null>(
    null,
  );
  const [drawerNotesDraft, setDrawerNotesDraft] = useState('');
  const savedFlagNotesByIdRef = useRef(
    new Map<string, NonNullable<ReconFlagRow['notes']>>(),
  );

  const [pendingDrawerAction, setPendingDrawerAction] =
    useState<DrawerTransitionAction | null>(null);

  const [transitionFlag] = useMutation(TRANSITION_FLAG);
  const [updateNotes] = useMutation(UPDATE_FLAG_NOTES);

  const persistNotesDraftIfNeeded = useCallback(async (): Promise<boolean> => {
    if (!activeDrawerFlag) {
      return false;
    }
    if (!hasUnsavedFlagNoteDraft(drawerNotesDraft, activeDrawerFlag.notes)) {
      return true;
    }
    try {
      const { data, errors } = await updateNotes({
        variables: {
          input: {
            flagId: activeDrawerFlag.id,
            notes: drawerNotesDraft.trim(),
          },
        },
      });
      if (errors?.length || !data?.updateFlagNotes?.flag?.id) {
        showToast({
          severity: 'error',
          message: t('reconFlags.drawer.notesSaveError'),
        });
        return false;
      }
      const savedNotes = data.updateFlagNotes.flag.notes ?? [];
      savedFlagNotesByIdRef.current.set(activeDrawerFlag.id, savedNotes);
      setActiveDrawerFlag((prev) =>
        prev == null ? null : { ...prev, notes: savedNotes },
      );
      setDrawerNotesDraft('');
      return true;
    } catch {
      showToast({
        severity: 'error',
        message: t('reconFlags.drawer.notesSaveError'),
      });
      return false;
    }
  }, [activeDrawerFlag, drawerNotesDraft, showToast, t, updateNotes]);

  const transitionDrawerFlag = useCallback(
    async (
      action: DrawerTransitionAction,
      transition: FlagTransition,
      messages: { success: string; error: string },
    ): Promise<void> => {
      if (!activeDrawerFlag) {
        return;
      }
      setPendingDrawerAction(action);
      try {
        const notesSaved = await persistNotesDraftIfNeeded();
        if (!notesSaved) {
          return;
        }
        const { data: transitionData, errors: transitionErrors } =
          await transitionFlag({
            variables: {
              input: {
                flagId: activeDrawerFlag.id,
                transition,
              },
            },
          });
        if (
          transitionErrors?.length ||
          !transitionData?.transitionFlag?.flag?.id
        ) {
          showToast({
            severity: 'error',
            message: messages.error,
          });
          return;
        }
        showToast({
          severity: 'success',
          message: messages.success,
        });
        await refetchAfterFlagMutation();
        setActiveDrawerFlag(null);
      } catch {
        showToast({
          severity: 'error',
          message: messages.error,
        });
      } finally {
        setPendingDrawerAction(null);
      }
    },
    [
      activeDrawerFlag,
      persistNotesDraftIfNeeded,
      refetchAfterFlagMutation,
      showToast,
      transitionFlag,
    ],
  );

  const onResolveDrawerFlag = useCallback(
    () =>
      transitionDrawerFlag('resolve', FlagTransition.Fixed, {
        success: t('reconFlags.drawer.resolveSuccess'),
        error: t('reconFlags.drawer.resolveError'),
      }),
    [t, transitionDrawerFlag],
  );

  const onDismissDrawerFlag = useCallback(
    () =>
      transitionDrawerFlag('dismiss', FlagTransition.Trivial, {
        success: t('reconFlags.drawer.dismissSuccess'),
        error: t('reconFlags.drawer.dismissError'),
      }),
    [t, transitionDrawerFlag],
  );

  const openDrawer = useCallback((row: ReconFlagRow) => {
    const cachedNotes = savedFlagNotesByIdRef.current.get(row.id);
    if (
      cachedNotes == null ||
      areFlagNotesAtLeastAsFresh(row.notes, cachedNotes)
    ) {
      if (cachedNotes != null) {
        savedFlagNotesByIdRef.current.delete(row.id);
      }
      setActiveDrawerFlag(row);
      return;
    }
    setActiveDrawerFlag({ ...row, notes: cachedNotes });
  }, []);

  const onNotesSaved = useCallback(
    async (notes: ReconFlagRow['notes']) => {
      const savedNotes = notes ?? [];
      setActiveDrawerFlag((prev) => {
        if (prev == null) {
          return null;
        }
        savedFlagNotesByIdRef.current.set(prev.id, savedNotes);
        return { ...prev, notes: savedNotes };
      });
      setDrawerNotesDraft('');
      try {
        await refetchGrid();
      } catch {
        // Best-effort grid sync; savedFlagNotesByIdRef keeps reopen correct.
      }
    },
    [refetchGrid],
  );

  useEffect(() => {
    setDrawerNotesDraft('');
    setPendingDrawerAction(null);
  }, [activeDrawerFlag?.id]);

  const resolveLoading = pendingDrawerAction === 'resolve';
  const dismissLoading = pendingDrawerAction === 'dismiss';

  const drawerProps = useMemo(
    () => ({
      sys,
      flag: activeDrawerFlag,
      isOpen: activeDrawerFlag !== null,
      onClose: () => setActiveDrawerFlag(null),
      notesDraft: drawerNotesDraft,
      onNotesDraftChange: setDrawerNotesDraft,
      onNotesSaved,
      onResolve: onResolveDrawerFlag,
      onDismiss: onDismissDrawerFlag,
      resolveLoading,
      dismissLoading,
    }),
    [
      activeDrawerFlag,
      dismissLoading,
      drawerNotesDraft,
      onDismissDrawerFlag,
      onNotesSaved,
      onResolveDrawerFlag,
      resolveLoading,
      sys,
    ],
  );

  return {
    drawerProps,
    openDrawer,
  };
};
